import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';

const CATEGORIES = ['General', 'Safety', 'Customer Service', 'Technical Skills', 'Sales', 'Onboarding', 'Compliance', 'Equipment-specific'];
const SECTION_TYPES = [
  { k: 'text',  label: '📄 Text / SOP' },
  { k: 'image', label: '🖼️ Image' },
  { k: 'video', label: '🎬 Video' },
  { k: 'pdf',   label: '📑 PDF / Doc' },
];

export function Training({ user, toast }) {
  const isOwner = ['owner','admin','super_admin'].includes(user?.role);
  const [tab, setTab] = useState(isOwner ? 'library' : 'my');
  const tabs = [
    { k: 'my',      label: '🎓 My Training' },
    ...(isOwner ? [
      { k: 'library', label: '📚 Library' },
      { k: 'team',    label: '👥 Team Progress' },
    ] : []),
  ];
  return <div style={{ padding:24 }}>
    <h1 style={{ margin:'0 0 14px 0', fontSize:24, fontWeight:800 }}>Training & SOPs</h1>
    <div style={{ display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${G.cardBorder}`, overflowX:'auto' }}>
      {tabs.map(t => (
        <button key={t.k} onClick={()=>setTab(t.k)}
          style={{ background:'none', border:'none', color: tab===t.k?G.purple:G.textMuted,
            padding:'10px 16px', fontWeight: tab===t.k?700:500, cursor:'pointer',
            borderBottom: tab===t.k?`2px solid ${G.purple}`:'2px solid transparent', marginBottom:-1, whiteSpace:'nowrap' }}>
          {t.label}
        </button>
      ))}
    </div>
    {tab === 'my'      && <MyTrainingTab toast={toast}/>}
    {tab === 'library' && isOwner && <LibraryTab toast={toast} user={user}/>}
    {tab === 'team'    && isOwner && <TeamProgressTab toast={toast}/>}
  </div>;
}

function MyTrainingTab({ toast }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);
  const load = () => api('/api/training/my').then(setCourses).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);
  if (loading) return <Spinner sz={32}/>;
  return <div>
    {courses.length === 0 && <div style={{ ...styles.card, padding:30, textAlign:'center', color:G.textMuted }}>No training assigned yet.</div>}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
      {courses.map(c => {
        const total = c.sections.length;
        const done = c.my_progress?.sections_completed.length || 0;
        const pct = c.my_progress?.progress_pct || 0;
        const completed = c.my_progress?.completed_at;
        return <div key={c.id} style={{ ...styles.card, padding:0, overflow:'hidden', cursor:'pointer' }} onClick={()=>setOpening(c)}>
          {c.cover_url && <div style={{ height:140, backgroundImage:`url(${c.cover_url})`, backgroundSize:'cover', backgroundPosition:'center' }}/>}
          <div style={{ padding:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <Badge c={G.cyan} bg={G.cyan+'22'}>{c.category}</Badge>
              {completed && <Badge c={G.green} bg={G.greenLight}>✓ Completed</Badge>}
            </div>
            <strong style={{ fontSize:16 }}>{c.title}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4, lineHeight:1.5, minHeight:36 }}>{c.description || ''}</div>
            <div style={{ marginTop:12, height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%', background: completed?G.green:G.purple, transition:'.3s' }}/>
            </div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>{done}/{total} sections · {pct}%</div>
          </div>
        </div>;
      })}
    </div>
    {opening && <CourseViewer course={opening} onClose={()=>setOpening(null)} onUpdate={()=>{ load(); }} toast={toast}/>}
  </div>;
}

function CourseViewer({ course, onClose, onUpdate, toast }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [completed, setCompleted] = useState(course.my_progress?.sections_completed || []);
  const sec = course.sections[sectionIdx];
  const isLast = sectionIdx >= course.sections.length - 1;
  const markDone = async () => {
    try {
      const r = await api(`/api/training/courses/${course.id}/section-complete`, { method:'POST', body:{ sectionIdx }});
      setCompleted(r.completed);
      if (r.isDone) { toast('🎓 Course complete!'); onUpdate?.(); onClose(); return; }
      if (!isLast) setSectionIdx(sectionIdx + 1);
      onUpdate?.();
    } catch(e) { toast(e.message,'error'); }
  };
  if (!sec) return null;
  return <Modal open onClose={onClose} width={900}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <div>
        <h2 style={{ margin:0 }}>{course.title}</h2>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Section {sectionIdx+1} of {course.sections.length}: {sec.title}</div>
      </div>
      <Btn onClick={onClose}>✕</Btn>
    </div>
    <div style={{ display:'flex', gap:14 }}>
      <div style={{ width:200, flexShrink:0 }}>
        {course.sections.map((s, i) => (
          <div key={i} onClick={()=>setSectionIdx(i)} style={{
            padding:'10px 12px', borderRadius:8, cursor:'pointer', marginBottom:4,
            background: i===sectionIdx ? G.purple+'22' : 'transparent',
            color: i===sectionIdx ? G.purple : G.text, fontSize:13,
            display:'flex', alignItems:'center', gap:8,
          }}>
            <span>{completed.includes(i)?'✅':'○'}</span>
            <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.title || `Section ${i+1}`}</span>
          </div>
        ))}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <SectionViewer section={sec}/>
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:18 }}>
          <Btn disabled={sectionIdx===0} onClick={()=>setSectionIdx(sectionIdx-1)}>← Previous</Btn>
          <Btn v="primary" onClick={markDone}>
            {completed.includes(sectionIdx) ? (isLast ? 'Finish course' : 'Next →') : (isLast ? '✓ Mark complete & finish' : '✓ Mark complete + next')}
          </Btn>
        </div>
      </div>
    </div>
  </Modal>;
}

function SectionViewer({ section }) {
  if (section.type === 'video') return <video src={section.url} controls style={{ width:'100%', maxHeight:480, borderRadius:10, background:'#000' }}/>;
  if (section.type === 'image') return <img src={section.url} alt="" style={{ width:'100%', maxHeight:480, borderRadius:10, objectFit:'contain', background:G.input }}/>;
  if (section.type === 'pdf')   return <iframe src={section.url} style={{ width:'100%', height:520, borderRadius:10, border:`1px solid ${G.cardBorder}` }}/>;
  return <div style={{ ...styles.card, padding:18, whiteSpace:'pre-wrap', lineHeight:1.7 }}>{section.body || section.text || ''}</div>;
}

function LibraryTab({ toast, user }) {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const load = () => api('/api/training/courses').then(setCourses).catch(()=>{});
  useEffect(() => { load(); }, []);
  const remove = async (c) => { if (!confirm(`Delete "${c.title}"?`)) return; try { await api(`/api/training/courses/${c.id}`, { method:'DELETE' }); load(); toast('Deleted'); } catch(e){ toast(e.message,'error'); } };
  return <div>
    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
      <div style={{ color:G.textMuted, fontSize:13 }}>Build courses with text, photos, videos, PDFs. Publish to make available to your team — then assign to specific people with a due date.</div>
      <Btn v="primary" onClick={()=>setEditing({ title:'', description:'', category:'General', sections:[], status:'draft' })}>+ New Course</Btn>
    </div>
    <table style={{ width:'100%', borderCollapse:'collapse' }}>
      <thead><tr>{['Title','Category','Sections','Status','Created',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
      <tbody>{courses.map(c => (
        <tr key={c.id} onClick={()=>setEditing(c)} style={{ cursor:'pointer' }}>
          <td style={{ ...styles.td, fontWeight:600 }}>{c.title}</td>
          <td style={styles.td}>{c.category}</td>
          <td style={styles.td}>{c.sections.length}</td>
          <td style={styles.td}><Badge c={c.status==='published'?G.green:G.textMuted} bg={(c.status==='published'?G.green:G.textMuted)+'22'}>{c.status}</Badge></td>
          <td style={styles.td}>{new Date(c.created_at).toLocaleDateString()}</td>
          <td style={styles.td} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', gap:6 }}>
              {c.status === 'published' && <Btn sz="xs" v="primary" onClick={()=>setAssigning(c)}>👥 Assign</Btn>}
              <Btn sz="xs" v="danger" onClick={()=>remove(c)}>×</Btn>
            </div>
          </td>
        </tr>
      ))}</tbody>
    </table>
    {courses.length === 0 && <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:14 }}>No courses yet. Build your first SOP or training module.</div>}
    {editing && <CourseEditor course={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }} toast={toast}/>}
    {assigning && <AssignCourseModal course={assigning} onClose={()=>setAssigning(null)} toast={toast}/>}
  </div>;
}

function AssignCourseModal({ course, onClose, toast }) {
  const [users, setUsers] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dueAt, setDueAt] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api('/api/team').then(r => setUsers(r.team || r || [])).catch(()=>setUsers([]));
  }, []);
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const assign = async () => {
    if (selected.size === 0) return toast('Pick at least one team member', 'warn');
    setSaving(true);
    try {
      const r = await api(`/api/training/courses/${course.id}/assign`, { method:'POST', body:{
        userIds: Array.from(selected),
        dueAt: dueAt || null,
      }});
      toast(`✓ Assigned "${r.courseTitle}" to ${r.assigned} ${r.assigned===1?'person':'people'}`);
      onClose();
    } catch(e) { toast(e.message, 'error'); setSaving(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 6px' }}>👥 Assign Training</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Course: <strong style={{ color:G.text }}>{course.title}</strong></div>
    <Field label="Due date (optional)">
      <Input type="date" value={dueAt} onChange={e=>setDueAt(e.target.value)}/>
    </Field>
    <div style={{ marginTop:10, marginBottom:8, fontSize:12, color:G.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em' }}>Assign to:</div>
    {!users ? <Spinner sz={20}/> : users.length === 0 ? <div style={{ color:G.textMuted, fontSize:13 }}>No team members. Add some in Team first.</div> : <div style={{ maxHeight:280, overflow:'auto', border:`1px solid ${G.border}`, borderRadius:8 }}>
      {users.map(u => (
        <label key={u.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer', borderBottom:`1px solid ${G.cardBorder}` }}>
          <input type="checkbox" checked={selected.has(u.id)} onChange={()=>toggle(u.id)}/>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>{u.first_name||u.firstName||u.name} {u.last_name||u.lastName||''}</div>
            <div style={{ fontSize:11, color:G.textMuted }}>{u.role} · {u.email}</div>
          </div>
        </label>
      ))}
    </div>}
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:14 }}>
      <span style={{ fontSize:13, color:G.textMuted }}>{selected.size} selected</span>
      <div style={{ display:'flex', gap:10 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={assign} disabled={saving || selected.size===0}>{saving ? <Spinner sz={14}/> : 'Assign'}</Btn>
      </div>
    </div>
  </Modal>;
}

function CourseEditor({ course, onClose, onSaved, toast }) {
  const [f, setF] = useState(course);
  const isNew = !f.id;
  const setSec = (i, patch) => {
    const next = [...f.sections];
    next[i] = { ...next[i], ...patch };
    setF({ ...f, sections: next });
  };
  const addSection = (type) => {
    const sec = { type, title: `New ${type} section` };
    if (type === 'text') sec.body = '';
    else sec.url = '';
    setF({ ...f, sections: [...(f.sections||[]), sec] });
  };
  const removeSection = (i) => setF({ ...f, sections: f.sections.filter((_, idx) => idx !== i) });
  const moveSection = (i, dir) => {
    const next = [...f.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setF({ ...f, sections: next });
  };
  const uploadAt = async (i, file) => {
    if (!file) return;
    toast('Uploading…');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api('/api/uploads', { method:'POST', body:{ name:file.name, mime:file.type, base64: reader.result }});
        setSec(i, { url: r.url });
        toast('✓ Uploaded');
      } catch(e) { toast(e.message,'error'); }
    };
    reader.readAsDataURL(file);
  };
  const uploadCover = async (file) => {
    if (!file) return;
    toast('Uploading cover…');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api('/api/uploads', { method:'POST', body:{ name:file.name, mime:file.type, base64: reader.result }});
        setF({ ...f, cover_url: r.url });
        toast('✓ Cover uploaded');
      } catch(e) { toast(e.message,'error'); }
    };
    reader.readAsDataURL(file);
  };
  const save = async (publish = false) => {
    const payload = { ...f, status: publish ? 'published' : f.status };
    try {
      if (isNew) {
        const r = await api('/api/training/courses', { method:'POST', body: payload });
        if (r.id) onSaved();
      } else {
        await api(`/api/training/courses/${f.id}`, { method:'PATCH', body: payload });
        onSaved();
      }
    } catch(e) { toast(e.message,'error'); }
  };
  return <Modal open onClose={onClose} width={900}>
    <h2 style={{ margin:'0 0 14px' }}>{isNew ? 'New Course' : 'Edit: ' + f.title}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
      <Field label="Title *"><Input value={f.title||''} onChange={e=>setF({...f, title:e.target.value})}/></Field>
      <Field label="Category">
        <Select value={f.category||'General'} onChange={e=>setF({...f, category:e.target.value})}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label="Description" style={{ gridColumn:'1/-1' }}>
        <Textarea value={f.description||''} onChange={e=>setF({...f, description:e.target.value})} style={{ minHeight:60 }}/>
      </Field>
      <Field label="Cover image" style={{ gridColumn:'1/-1' }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {f.cover_url && <img src={f.cover_url} style={{ width:120, height:60, objectFit:'cover', borderRadius:6 }}/>}
          <label style={{ ...styles.card, padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
            {f.cover_url ? 'Replace cover' : '+ Upload cover'}
            <input type="file" accept="image/*" style={{ display:'none' }} onChange={e=>uploadCover(e.target.files?.[0])}/>
          </label>
        </div>
      </Field>
    </div>

    <h3 style={{ margin:'18px 0 10px', fontSize:15 }}>Sections ({f.sections?.length||0})</h3>
    <div>
      {(f.sections||[]).map((s, i) => (
        <div key={i} style={{ ...styles.card, padding:12, marginBottom:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <Badge c={G.purple} bg={G.purpleDim}>{SECTION_TYPES.find(t=>t.k===s.type)?.label || s.type}</Badge>
            <Input value={s.title||''} onChange={e=>setSec(i, { title:e.target.value })} placeholder="Section title" style={{ flex:1 }}/>
            <Btn sz="xs" onClick={()=>moveSection(i, -1)} disabled={i===0}>↑</Btn>
            <Btn sz="xs" onClick={()=>moveSection(i, 1)} disabled={i===f.sections.length-1}>↓</Btn>
            <Btn sz="xs" v="danger" onClick={()=>removeSection(i)}>×</Btn>
          </div>
          {s.type === 'text' && <Textarea value={s.body||''} onChange={e=>setSec(i, { body:e.target.value })} placeholder="Write the SOP / training content. Markdown supported." style={{ minHeight:120 }}/>}
          {s.type !== 'text' && <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {s.url && s.type === 'image' && <img src={s.url} style={{ width:100, height:60, objectFit:'cover', borderRadius:6 }}/>}
            {s.url && s.type === 'video' && <video src={s.url} style={{ width:100, height:60, borderRadius:6 }} muted/>}
            {s.url && s.type === 'pdf' && <span style={{ fontSize:12, color:G.cyan }}>📑 PDF uploaded</span>}
            <label style={{ ...styles.card, padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
              {s.url ? 'Replace file' : `+ Upload ${s.type}`}
              <input type="file" accept={s.type==='image'?'image/*':s.type==='video'?'video/*':'application/pdf'} style={{ display:'none' }}
                onChange={e=>uploadAt(i, e.target.files?.[0])}/>
            </label>
            {s.url && <a href={s.url} target="_blank" rel="noreferrer" style={{ fontSize:12, color:G.cyan }}>Open</a>}
          </div>}
        </div>
      ))}
    </div>

    <div style={{ display:'flex', gap:8, marginTop:10 }}>
      {SECTION_TYPES.map(t => (
        <Btn key={t.k} sz="sm" onClick={()=>addSection(t.k)}>{t.label}</Btn>
      ))}
    </div>

    <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:14, borderTop:`1px solid ${G.cardBorder}` }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <div style={{ display:'flex', gap:10 }}>
        <Btn onClick={()=>save(false)}>💾 Save Draft</Btn>
        <Btn v="primary" onClick={()=>save(true)} disabled={!f.title || !f.sections?.length}>🚀 Publish</Btn>
      </div>
    </div>
  </Modal>;
}

function TeamProgressTab({ toast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api('/api/training/team').then(setRows).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false)); }, []);
  if (loading) return <Spinner sz={32}/>;
  if (!rows.length) return <div style={{ ...styles.card, padding:30, textAlign:'center', color:G.textMuted }}>No team progress yet. Publish a course and your team will start showing up here.</div>;
  return <table style={{ width:'100%', borderCollapse:'collapse' }}>
    <thead><tr>{['Employee','Course','Category','Progress','Started','Completed'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{rows.map(r => (
      <tr key={r.id}>
        <td style={{ ...styles.td, fontWeight:600 }}>{r.first_name} {r.last_name}</td>
        <td style={styles.td}>{r.course_title}</td>
        <td style={styles.td}>{r.category}</td>
        <td style={styles.td}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:80, height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:`${r.progress_pct}%`, height:'100%', background: r.completed_at?G.green:G.purple }}/>
            </div>
            <span style={{ fontSize:11, color:G.textMuted }}>{r.progress_pct}%</span>
          </div>
        </td>
        <td style={styles.td}>{new Date(r.started_at).toLocaleDateString()}</td>
        <td style={styles.td}>{r.completed_at ? <Badge c={G.green} bg={G.greenLight}>✓ {new Date(r.completed_at).toLocaleDateString()}</Badge> : <span style={{ color:G.textMuted, fontSize:12 }}>in progress</span>}</td>
      </tr>
    ))}</tbody>
  </table>;
}
